// ════════════════════════════════════════════════════════════════════
// HEALTH HOUSEHOLD PANEL
//
// Mounts inside HealthClientProfileModal. Surfaces:
//   • Household roster (primary + spouse + dependents) — Q1 of the spec
//   • Per-policy beneficiaries — Q2b
//   • Renewal countdown widgets (STM cliffs, AEP windows, expired) — Q4
//   • Compliance disclosure trail (most-recent first) — Q6
//
// Reads /api/health/household once on mount, gets:
//   { household, members, policies, beneficiaries, renewals }
// Reads /api/health/disclosures separately for the trail.
//
// Writes go through PATCH/POST on the same endpoints.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Users, Shield, Calendar, AlertTriangle, FileCheck, Plus, Trash2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

export default function HealthHouseholdPanel({ clientId, currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);
  const [disclosures, setDisclosures] = React.useState([]);
  const [showAddMember, setShowAddMember] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const [hh, dis] = await Promise.all([
        apiFetch(`/api/health/household?client_id=${encodeURIComponent(clientId)}`),
        apiFetch(`/api/health/disclosures?client_id=${encodeURIComponent(clientId)}`).catch(() => ({ disclosures: [] })),
      ]);
      setData(hh);
      setDisclosures(dis.disclosures || []);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  React.useEffect(() => { load(); }, [load]);

  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState(null);
  const createHousehold = async () => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      if (!clientId) {
        throw new Error('No client selected — reopen this profile from the Clients list.');
      }
      const clientName = data?.client?.name
        || data?.household?.name
        || 'Household';
      const r = await apiFetch('/api/health/household', {
        method: 'POST',
        body: JSON.stringify({
          action: 'upsert_household',
          name: `${clientName} household`,
          primary_client_id: clientId,
        }),
      });
      if (r?.household?.id && clientId) {
        // Also add the primary client as a household_member so the
        // household roster shows them immediately. Ignore errors —
        // even without the member row, load() still finds the
        // household via primary_client_id.
        try {
          await apiFetch('/api/health/household', {
            method: 'POST',
            body: JSON.stringify({
              action: 'upsert_member',
              household_id: r.household.id,
              client_id: clientId,
              role: 'primary',
              on_primary_policy: true,
            }),
          });
        } catch { /* non-fatal */ }
      }
      showToast('Household created.');
      await load();
    } catch (err) {
      const msg = String(err.message || err);
      setCreateError(msg);
      showToast(`Could not create: ${msg}`);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 p-4">Loading household…</div>;
  }
  if (error) {
    return <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800">{error}</div>;
  }

  // No household yet → CTA to create.
  if (!data?.household) {
    return (
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-5 text-center">
        <Users size={20} className="mx-auto text-slate-400 mb-2"/>
        <div className="text-sm text-slate-700 font-medium">No household record yet</div>
        <div className="text-xs text-slate-500 mt-1">A family ACA + the kid's dental + spouse's accident plan all belong to ONE household.</div>
        <button
          onClick={createHousehold}
          disabled={creating || !clientId}
          className={`mt-3 text-white text-xs font-bold px-4 py-2 rounded-md ${
            creating || !clientId
              ? 'bg-slate-400 cursor-not-allowed'
              : 'bg-slate-900 hover:bg-slate-800'
          }`}
        >
          {creating ? 'Creating…' : 'Create household'}
        </button>
        {createError && (
          <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2 max-w-md mx-auto">
            {createError}
          </div>
        )}
      </div>
    );
  }

  const { household, members, policies, beneficiaries, renewals } = data;

  return (
    <div className="space-y-4">
      {/* ── Renewal countdown alerts (top of section — most urgent) ── */}
      <RenewalAlerts renewals={renewals}/>

      {/* ── Household roster ── */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-slate-600"/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Household · {household.name}</span>
            <span className="text-xs text-slate-500">({members.length} {members.length === 1 ? 'member' : 'members'})</span>
          </div>
          <button onClick={() => setShowAddMember(!showAddMember)} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
            <Plus size={10}/> Add member
          </button>
        </div>
        {showAddMember && (
          <AddMemberForm
            householdId={household.id}
            onAdded={() => { setShowAddMember(false); load(); }}
            onCancel={() => setShowAddMember(false)}
            showToast={showToast}
          />
        )}
        {members.length === 0 ? (
          <div className="px-4 py-3 text-sm text-slate-500 italic">No additional members yet. Add spouse / dependents.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {members.map(m => (
              <MemberRow key={m.id} member={m} onChanged={load} showToast={showToast}/>
            ))}
          </div>
        )}
      </div>

      {/* ── Per-policy beneficiaries ── */}
      {policies.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
            <Shield size={14} className="text-slate-600"/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Beneficiaries · per policy</span>
          </div>
          <div className="divide-y divide-slate-100">
            {policies.map(p => {
              const bens = beneficiaries.filter(b => b.policy_id === p.id);
              return (
                <PolicyBeneficiaries
                  key={p.id}
                  policy={p}
                  beneficiaries={bens}
                  members={members}
                  onChanged={load}
                  showToast={showToast}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── Compliance disclosure trail ── */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <FileCheck size={14} className="text-slate-600"/>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Compliance trail · disclosures shown to client</span>
        </div>
        {disclosures.length === 0 ? (
          <div className="px-4 py-3 text-sm text-slate-500 italic">No disclosures logged yet. Use the disclosure widgets in the quoting flow to record each one shown to the client.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {disclosures.slice(0, 20).map(d => (
              <div key={d.id} className="px-4 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                    {String(d.disclosure_kind).replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-slate-500">
                    {d.acknowledged_at ? `Acknowledged ${new Date(d.acknowledged_at).toLocaleString()}` : 'Not yet acknowledged'} · via {d.acknowledged_via || 'unknown'}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-1 line-clamp-2">{d.disclosure_text}</div>
                {d.artifact_url && (
                  <a href={d.artifact_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-sky-700 hover:underline mt-1 inline-block">
                    View signed artifact →
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Renewal alerts — surface STM cliffs, AEP windows, expirations ──
function RenewalAlerts({ renewals = [] }) {
  // Filter to anything with urgency != 'ok'.
  const urgent = renewals.filter(r => r.urgency && r.urgency !== 'ok');
  if (urgent.length === 0) return null;
  const tone = (u) =>
    u === 'expired'      ? 'bg-rose-50 border-rose-300 text-rose-900' :
    u === 'critical'     ? 'bg-rose-50 border-rose-300 text-rose-900' :
    u === 'stm_cliff'    ? 'bg-amber-50 border-amber-300 text-amber-900' :
    u === 'aep_window'   ? 'bg-sky-50 border-sky-300 text-sky-900' :
                            'bg-amber-50 border-amber-300 text-amber-900';
  return (
    <div className="space-y-2">
      {urgent.slice(0, 5).map(r => (
        <div key={r.policy_id} className={`border-2 rounded-md px-3 py-2 text-sm flex items-center gap-2 ${tone(r.urgency)}`}>
          <AlertTriangle size={14} className="shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-bold">
              {r.carrier} · {r.plan_name}
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-current opacity-75">{r.urgency}</span>
            </div>
            <div className="text-xs">
              {r.days_until_expiration < 0
                ? `Expired ${Math.abs(r.days_until_expiration)} days ago`
                : `${r.days_until_expiration} days until ${r.renewal_date ? 'renewal' : 'term ends'} (${r.next_expiration_date})`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddMemberForm({ householdId, onAdded, onCancel, showToast }) {
  const [role, setRole]       = React.useState('child');
  const [firstName, setFN]    = React.useState('');
  const [lastName, setLN]     = React.useState('');
  const [dob, setDob]         = React.useState('');
  const [gender, setGender]   = React.useState('');
  const [onPrimary, setOnPrimary] = React.useState(true);
  const [busy, setBusy]       = React.useState(false);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim()) { showToast('Name required.'); return; }
    setBusy(true);
    try {
      await apiFetch('/api/health/household', {
        method: 'POST',
        body: JSON.stringify({
          action: 'upsert_member',
          household_id: householdId,
          role,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          dob: dob || null,
          gender: gender || null,
          on_primary_policy: onPrimary,
        }),
      });
      onAdded();
    } catch (err) {
      showToast(`Add failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <select value={role} onChange={e => setRole(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
          <option value="spouse">Spouse</option>
          <option value="child">Child</option>
          <option value="dependent">Other dependent</option>
          <option value="other">Other</option>
        </select>
        <input value={firstName} onChange={e => setFN(e.target.value)} placeholder="First name" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
        <input value={lastName}  onChange={e => setLN(e.target.value)} placeholder="Last name"  className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
        <input type="date" value={dob} onChange={e => setDob(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
        <select value={gender} onChange={e => setGender(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
          <option value="">Gender</option>
          <option value="F">F</option>
          <option value="M">M</option>
          <option value="X">X</option>
        </select>
      </div>
      <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
        <input type="checkbox" checked={onPrimary} onChange={e => setOnPrimary(e.target.checked)}/>
        On the primary policy (ACA family enrollment, etc.)
      </label>
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded-md">{busy ? 'Adding…' : 'Add'}</button>
        <button onClick={onCancel} className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

function MemberRow({ member, onChanged, showToast }) {
  const remove = async () => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: `Remove ${member.first_name || member.client?.name || 'this member'} from the household?`,
      confirmLabel: 'Remove member', danger: true,
    }) : Promise.resolve(window.confirm(`Remove this member from the household?`)));
    if (!ok) return;
    try {
      await apiFetch('/api/health/household', { method: 'DELETE', body: JSON.stringify({ action: 'remove_member', id: member.id }) });
      onChanged();
    } catch (err) {
      showToast(`Remove failed: ${err.message || err}`);
    }
  };
  const displayName = member.client?.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || '(unnamed)';
  return (
    <div className="px-4 py-2 flex items-center gap-2">
      <div className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 w-20 text-center">
        {member.role}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{displayName}</div>
        <div className="text-xs text-slate-500">
          {member.dob && `DOB ${member.dob}`}
          {member.gender && ` · ${member.gender}`}
          {!member.on_primary_policy && ' · NOT on primary policy'}
        </div>
      </div>
      <button onClick={remove} className="text-slate-400 hover:text-rose-700">
        <Trash2 size={14}/>
      </button>
    </div>
  );
}

function PolicyBeneficiaries({ policy, beneficiaries, members, onChanged, showToast }) {
  const [adding, setAdding] = React.useState(false);
  const [name, setName]     = React.useState('');
  const [relationship, setRelationship] = React.useState('Spouse');
  const [pct, setPct]       = React.useState('100');
  const [contingent, setContingent] = React.useState(false);

  const primary = beneficiaries.filter(b => !b.is_contingent);
  const conting = beneficiaries.filter(b => b.is_contingent);
  const primaryTotal = primary.reduce((s, b) => s + Number(b.percentage || 0), 0);
  const totalMismatch = primary.length > 0 && Math.abs(primaryTotal - 100) > 0.01;

  const add = async () => {
    if (!name.trim()) { showToast('Name required.'); return; }
    try {
      await apiFetch('/api/health/household', {
        method: 'POST',
        body: JSON.stringify({
          action: 'upsert_beneficiary',
          policy_id: policy.id,
          name: name.trim(),
          relationship,
          percentage: Number(pct) || 0,
          is_contingent: contingent,
        }),
      });
      setName(''); setPct('100'); setContingent(false);
      setAdding(false);
      onChanged();
    } catch (err) {
      showToast(`Add failed: ${err.message || err}`);
    }
  };

  const remove = async (id) => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: 'Remove this beneficiary?',
      confirmLabel: 'Remove', danger: true,
    }) : Promise.resolve(window.confirm('Remove this beneficiary?')));
    if (!ok) return;
    try {
      await apiFetch('/api/health/household', { method: 'DELETE', body: JSON.stringify({ action: 'remove_beneficiary', id }) });
      onChanged();
    } catch (err) {
      showToast(`Remove failed: ${err.message || err}`);
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="text-sm font-semibold text-slate-900">
          {policy.carrier} · {policy.plan_name}
          <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{policy.plan_type}</span>
        </div>
        <button onClick={() => setAdding(!adding)} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
          <Plus size={10}/> Add beneficiary
        </button>
      </div>
      {totalMismatch && (
        <div className="text-xs text-amber-700 inline-flex items-center gap-1 mb-1">
          <AlertCircle size={11}/> Primary beneficiary % sums to {primaryTotal}% (should be 100%)
        </div>
      )}
      {adding && (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-2 space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <select value={relationship} onChange={e => setRelationship(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              <option>Spouse</option><option>Child</option><option>Parent</option><option>Sibling</option><option>Trust</option><option>Estate</option><option>Other</option>
            </select>
            <input value={pct} onChange={e => setPct(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="%" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
              <input type="checkbox" checked={contingent} onChange={e => setContingent(e.target.checked)}/>
              Contingent
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md">Save</button>
            <button onClick={() => setAdding(false)} className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}
      {primary.length === 0 && conting.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No beneficiaries on file for this policy yet.</div>
      ) : (
        <div className="space-y-1">
          {primary.map(b => <BeneficiaryRow key={b.id} ben={b} onRemove={() => remove(b.id)}/>)}
          {conting.length > 0 && <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 pt-1">Contingent</div>}
          {conting.map(b => <BeneficiaryRow key={b.id} ben={b} onRemove={() => remove(b.id)}/>)}
        </div>
      )}
    </div>
  );
}

function BeneficiaryRow({ ben, onRemove }) {
  return (
    <div className="text-xs flex items-center gap-2">
      <span className="font-bold text-slate-900 min-w-0 flex-1 truncate">{ben.name}</span>
      <span className="text-slate-500">{ben.relationship || ''}</span>
      <span className="tabular-nums font-bold text-slate-700 w-12 text-right">{Number(ben.percentage).toFixed(0)}%</span>
      <button onClick={onRemove} className="text-slate-400 hover:text-rose-700">
        <Trash2 size={11}/>
      </button>
    </div>
  );
}
